import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, Lock, Mail, Building2, Shield, Eye, EyeOff, Save, Check } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import { PageHeader } from '../../components/ui'
import { Panel, Badge, Button, type BadgeTone } from '../../design-system'

const alterarSenhaSchema = z.object({
  senhaAtual: z.string().min(1, 'Senha atual é obrigatória'),
  novaSenha: z.string()
    .min(6, 'Nova senha deve ter pelo menos 6 caracteres')
    .regex(/[A-Z]/, 'Deve conter pelo menos uma letra maiúscula')
    .regex(/[a-z]/, 'Deve conter pelo menos uma letra minúscula')
    .regex(/[0-9]/, 'Deve conter pelo menos um número'),
  confirmarSenha: z.string().min(1, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.novaSenha === data.confirmarSenha, {
  message: 'As senhas não conferem',
  path: ['confirmarSenha'],
})

type AlterarSenhaForm = z.infer<typeof alterarSenhaSchema>

export default function Perfil() {
  const { user } = useAuth()
  const { showSuccess, showApiError } = useNotification()
  const [showSenhaAtual, setShowSenhaAtual] = useState(false)
  const [showNovaSenha, setShowNovaSenha] = useState(false)
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [senhaAlterada, setSenhaAlterada] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AlterarSenhaForm>({
    resolver: zodResolver(alterarSenhaSchema),
    defaultValues: {
      senhaAtual: '',
      novaSenha: '',
      confirmarSenha: '',
    },
  })

  const onSubmit = async (data: AlterarSenhaForm) => {
    setIsSubmitting(true)
    try {
      await api.post('/auth/alterar-senha', {
        senhaAtual: data.senhaAtual,
        novaSenha: data.novaSenha,
      })
      showSuccess('Senha alterada com sucesso!')
      reset()
      setSenhaAlterada(true)
      setTimeout(() => setSenhaAlterada(false), 3000)
    } catch (error) {
      showApiError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getTipoUsuarioLabel = (tipo: string) => {
    switch (tipo) {
      case 'ADMIN':
        return 'Administrador'
      case 'GERENTE':
        return 'Gerente'
      case 'VENDEDOR':
        return 'Vendedor'
      default:
        return tipo
    }
  }

  const getTipoUsuarioTone = (tipo: string): BadgeTone => {
    switch (tipo) {
      case 'ADMIN':
        return 'primary'
      case 'GERENTE':
        return 'info'
      case 'VENDEDOR':
        return 'success'
      default:
        return 'neutral'
    }
  }

  const inputClass = (hasError?: boolean) =>
    `w-full h-10 px-3 pr-10 rounded-xl border bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-400 ${
      hasError ? 'border-rose-400' : 'border-slate-200'
    }`

  return (
    <div>
      <PageHeader
        title="Meu Perfil"
        subtitle="Visualize suas informações e altere sua senha"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações do Usuário */}
        <Panel title="Informações Pessoais" icon={<User className="w-4 h-4" />}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-2xl bg-primary-600 flex items-center justify-center shrink-0">
              <span className="text-3xl font-bold text-white">
                {user?.nome?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900">{user?.nome}</h3>
              <Badge tone={getTipoUsuarioTone(user?.tipoUsuario || '')} className="mt-1">
                <Shield className="w-3 h-3" />
                {getTipoUsuarioLabel(user?.tipoUsuario || '')}
              </Badge>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">E-mail</p>
                <p className="text-slate-900">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Loja</p>
                <p className="text-slate-900">{user?.lojaNome || 'Todas as lojas'}</p>
              </div>
            </div>

            {user?.modulos && user.modulos.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">Módulos Habilitados</p>
                <div className="flex flex-wrap gap-2">
                  {user.modulos.map((modulo) => (
                    <Badge key={modulo} tone="neutral">
                      {modulo}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Panel>

        {/* Alterar Senha */}
        <Panel title="Alterar Senha" icon={<Lock className="w-4 h-4" />}>
          {senhaAlterada && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700">
              <Check className="w-5 h-5" />
              Senha alterada com sucesso!
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Senha Atual
              </label>
              <div className="relative">
                <input
                  type={showSenhaAtual ? 'text' : 'password'}
                  {...register('senhaAtual')}
                  className={inputClass(!!errors.senhaAtual)}
                  placeholder="Digite sua senha atual"
                />
                <button
                  type="button"
                  onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSenhaAtual ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.senhaAtual && (
                <p className="mt-1 text-sm text-rose-600">{errors.senhaAtual.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nova Senha
              </label>
              <div className="relative">
                <input
                  type={showNovaSenha ? 'text' : 'password'}
                  {...register('novaSenha')}
                  className={inputClass(!!errors.novaSenha)}
                  placeholder="Digite a nova senha"
                />
                <button
                  type="button"
                  onClick={() => setShowNovaSenha(!showNovaSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNovaSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.novaSenha && (
                <p className="mt-1 text-sm text-rose-600">{errors.novaSenha.message}</p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                Mínimo 6 caracteres, com letras maiúsculas, minúsculas e números
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <input
                  type={showConfirmarSenha ? 'text' : 'password'}
                  {...register('confirmarSenha')}
                  className={inputClass(!!errors.confirmarSenha)}
                  placeholder="Confirme a nova senha"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmarSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmarSenha && (
                <p className="mt-1 text-sm text-rose-600">{errors.confirmarSenha.message}</p>
              )}
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Alterando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Alterar Senha
                </>
              )}
            </Button>
          </form>
        </Panel>
      </div>
    </div>
  )
}
